import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, CssBaseline, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useThemeMode } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';
import NavBar from './components/NavBar';
import Footer from './components/Footer';
import AdminLayout from './components/AdminLayout';
import AuthCallbackGuard from './components/AuthCallbackGuard';
import AuthBootstrapScreen from './components/AuthBootstrapScreen';

import PlansPage from './pages/PlansPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import SubscriptionDetailPage from './pages/SubscriptionDetailPage';
import PaymentHistoryPage from './pages/PaymentHistoryPage';

import AdminDashboard from './pages/admin/AdminDashboard';
import SubscriptionManagement from './pages/admin/SubscriptionManagement';
import ProductManagement from './pages/admin/ProductManagement';
import DiscountManagement from './pages/admin/DiscountManagement';
import OverrideManagement from './pages/admin/OverrideManagement';
import PaymentManagement from './pages/admin/PaymentManagement';
import AuditLog from './pages/admin/AuditLog';
import ProviderFeeManagement from './pages/admin/ProviderFeeManagement';
import UsageAnalytics from './pages/admin/UsageAnalytics';
import PricingManager from './pages/admin/pricing/PricingManager';
import PlanBuilder from './pages/admin/pricing/builder/PlanBuilder';
import PricingHistory from './pages/admin/PricingHistory';
import BundleManagement from './pages/admin/BundleManagement';
import FinancialLedger from './pages/admin/FinancialLedger';
import ReconciliationDashboard from './pages/admin/ReconciliationDashboard';
import WorkflowMonitor from './pages/admin/WorkflowMonitor';
import WorkflowApprovals from './pages/admin/WorkflowApprovals';
import WorkflowEditor from './pages/admin/WorkflowEditor';
import WorkflowRunPage from './pages/admin/WorkflowRunPage';
import WorkflowRunPublic from './pages/WorkflowRunPublic';
import TaxRateManagement from './pages/admin/TaxRateManagement';
import TaxExemptionManagement from './pages/admin/TaxExemptionManagement';
import CustomerNotes from './pages/admin/CustomerNotes';
import AiUsageDashboard from './pages/admin/AiUsageDashboard';
import MockCheckoutPage from './pages/MockCheckoutPage';
import MockPortalPage from './pages/MockPortalPage';

// Theme is now managed by ThemeContext — provides light/dark mode toggle

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading, login, refreshSession, loggingOut } = useAuth();
  const [refreshAttempted, setRefreshAttempted] = React.useState(false);

  // If user lacks admin but route requires it, try refreshing the token once
  // (the stored token may be stale and missing updated roles)
  React.useEffect(() => {
    if (adminOnly && user && !user.isAdmin && !refreshAttempted) {
      setRefreshAttempted(true);
      refreshSession();
    }
  }, [adminOnly, user, refreshAttempted, refreshSession]);

  // Debug logging
  console.log('🔐 ProtectedRoute:', {
    adminOnly,
    loading,
    hasUser: !!user,
    email: user?.email,
    roles: user?.roles,
    isAdmin: user?.isAdmin,
    refreshAttempted,
    rawToken: localStorage.getItem('billing-access-token')?.substring(0, 50) + '...'
  });

  if (loading) return <AuthBootstrapScreen />;
  if (loggingOut) return <AuthBootstrapScreen message="Signing out…" />;
  if (!user) {
    login();
    return null;
  }
  // Wait for the refresh attempt before redirecting
  if (adminOnly && !user.isAdmin && !refreshAttempted) return <AuthBootstrapScreen />;
  if (adminOnly && !user.isAdmin) {
    console.warn('🔐 ProtectedRoute: User is not admin, redirecting to /plans. Token payload:', (() => {
      try {
        const t = localStorage.getItem('billing-access-token');
        return JSON.parse(atob(t.split('.')[1]));
      } catch { return 'parse failed'; }
    })());
    return <Navigate to="/plans" />;
  }

  return children;
}

function DebugAuth() {
  const { user, loading } = useAuth();
  const token = localStorage.getItem('billing-access-token');
  let decoded = null;
  try { decoded = token ? JSON.parse(atob(token.split('.')[1])) : null; } catch {}

  return (
    <div style={{ padding: '2rem', fontFamily: 'monospace', fontSize: '14px' }}>
      <h2>Auth Debug</h2>
      <p><strong>Loading:</strong> {String(loading)}</p>
      <p><strong>User from context:</strong> {user ? JSON.stringify({ email: user.email, roles: user.roles, isAdmin: user.isAdmin }) : 'null'}</p>
      <p><strong>Token in localStorage:</strong> {token ? token.substring(0, 50) + '...' : 'null'}</p>
      <p><strong>Decoded token:</strong></p>
      <pre>{decoded ? JSON.stringify(decoded, null, 2) : 'null'}</pre>
      <p><strong>Refresh token:</strong> {localStorage.getItem('billing-refresh-token') ? 'present' : 'null'}</p>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/plans" element={<PlansPage />} />

      {/* Authenticated */}
      <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
      <Route path="/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
      <Route path="/subscriptions" element={<ProtectedRoute><SubscriptionsPage /></ProtectedRoute>} />
      <Route path="/subscriptions/:id" element={<ProtectedRoute><SubscriptionDetailPage /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><PaymentHistoryPage /></ProtectedRoute>} />
      <Route path="/workflows/run/:workflowId" element={<ProtectedRoute><WorkflowRunPublic /></ProtectedRoute>} />

      {/* Admin — all wrapped in AdminLayout with sidebar navigation */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/subscriptions" element={<ProtectedRoute adminOnly><AdminLayout><SubscriptionManagement /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute adminOnly><AdminLayout><ProductManagement /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/discounts" element={<ProtectedRoute adminOnly><AdminLayout><DiscountManagement /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/overrides" element={<ProtectedRoute adminOnly><AdminLayout><OverrideManagement /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/payments" element={<ProtectedRoute adminOnly><AdminLayout><PaymentManagement /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/pricing" element={<ProtectedRoute adminOnly><AdminLayout><PlanBuilder /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/pricing/history" element={<ProtectedRoute adminOnly><AdminLayout><PricingHistory /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/bundles" element={<ProtectedRoute adminOnly><AdminLayout><BundleManagement /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/ledger" element={<ProtectedRoute adminOnly><AdminLayout><FinancialLedger /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/reconciliation" element={<ProtectedRoute adminOnly><AdminLayout><ReconciliationDashboard /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/usage" element={<ProtectedRoute adminOnly><AdminLayout><UsageAnalytics /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/workflows" element={<ProtectedRoute adminOnly><AdminLayout><WorkflowMonitor /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/workflows/approvals" element={<ProtectedRoute adminOnly><AdminLayout><WorkflowApprovals /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/workflows/editor" element={<ProtectedRoute adminOnly><AdminLayout><WorkflowEditor /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/workflows/editor/:workflowId" element={<ProtectedRoute adminOnly><AdminLayout><WorkflowEditor /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/workflows/run/:workflowId" element={<ProtectedRoute adminOnly><AdminLayout><WorkflowRunPage /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/notes" element={<ProtectedRoute adminOnly><AdminLayout><CustomerNotes /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/tax/rates" element={<ProtectedRoute adminOnly><AdminLayout><TaxRateManagement /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/tax/exemptions" element={<ProtectedRoute adminOnly><AdminLayout><TaxExemptionManagement /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/fees" element={<ProtectedRoute adminOnly><AdminLayout><ProviderFeeManagement /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute adminOnly><AdminLayout><AuditLog /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/ai-usage" element={<ProtectedRoute adminOnly><AdminLayout><AiUsageDashboard /></AdminLayout></ProtectedRoute>} />

      {/* Auth callback */}
      <Route path="/auth/callback" element={<AuthCallbackGuard />} />

      {/* Mock Stripe pages (development only) */}
      <Route path="/mock-checkout" element={<MockCheckoutPage />} />
      <Route path="/mock-portal" element={<MockPortalPage />} />

      {/* Debug: show token state without requiring auth */}
      <Route path="/debug-auth" element={<DebugAuth />} />

      {/* Default */}
      <Route path="/" element={<Navigate to="/plans" />} />
    </Routes>
  );
}

function AppShell() {
  const { theme } = useThemeMode();
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <CartProvider>
          {/* 999.15542: height:100dvh + overflow:hidden so the footer sits
              OUTSIDE the scrolled area and stays flush to the bottom of the
              viewport on every route. dvh (not vh) so mobile Safari's
              collapsing URL bar does not push the footer off-screen. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>
            <NavBar />
            {/* 999.15542: this is THE vertical scroll container. flex:1 +
                minHeight:0 so it fills the remaining height and can shrink.
                overflowY:auto !important — the !important is required because
                MUI's modal scroll-lock writes an inline overflow:initial onto
                the scroll container and never removes it, which would kill
                scrolling for the rest of the session. */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto !important', overflowX: 'hidden', backgroundColor: 'background.default' }}>
              <AppRoutes />
            </Box>
            <Footer />
          </Box>
        </CartProvider>
      </AuthProvider>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppShell />
    </ThemeProvider>
  );
}

export default App;
